import React, { useState } from "react";
import Hero from "./misc/Hero";
import PricingToggle from "./misc/PricingToggle";
import PricingPlans from "./misc/PricingPlans";
import { useSearchParams } from "react-router-dom";
import AddOnServices from "./misc/AddOnServices";
import ModalNew from "../../../components/Modal/ModalNew";
import YouTubeEmbed from "../../../components/elements/YouTubeEmbed";
import Navbar from "../../../components/Navbar2";
import AddOnsAi from "./misc/AddOnsAi";
// import LifecyclePhasesSection from "../misc/LifecyclePhasesSection";
import SelectedFeaturesFloating, {
  SelectedItem,
} from "../misc/SelectedFeaturesFloating";
import { Zap, Star, Crown } from "lucide-react";
import { offeringsService } from "../../../services/api/settings";
import { OfferingPlan } from "../../../store/types/settings";
import Skeleton from "../../../components/elements/Skeleton";

const PricingPage: React.FC = () => {
  const [billingType, setBillingType] = useState<
    "Monthly" | "Quarterly" | "Yearly"
  >(() => {
    const freq = new URLSearchParams(window.location.search).get("frequency");
    if (freq === "YEARLY") return "Yearly";
    if (freq === "QUARTERLY") return "Quarterly";
    return "Monthly";
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("Id");
  const urlTier = searchParams.get("tier");
  const [selectedTier, setSelectedTier] = useState<string | null>(urlTier);
  const [selectedDirectPlan, setSelectedDirectPlan] = useState<{
    [key: string]: string;
  }>({});
  const [features, setFeatures] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>(
    searchParams
      .get("featureIds")
      ?.split(",")
      .filter((id) => id) || [],
  );
  const [offeringPlans, setOfferingPlans] = useState<OfferingPlan[]>([]);
  const [offeringAddons, setOfferingAddons] = useState<any[]>([]);

  const [lifecyclePhases, setLifecyclePhases] = useState<any[]>([]);
  const [selectedLifecyclePhaseIds, setSelectedLifecyclePhaseIds] = useState<
    number[]
  >(
    searchParams
      .get("lifecyclePhaseIds")
      ?.split(",")
      .map(Number)
      .filter((id) => !isNaN(id)) || [],
  );

  const handleFeatureSelectionChange = (selectedIds: string[]) => {
    setSelectedFeatureIds(selectedIds);
    setSearchParams((prev) => {
      if (selectedIds.length > 0) {
        prev.set("featureIds", selectedIds.join(","));
      } else {
        prev.delete("featureIds");
      }
      return prev;
    });
  };

  // Fetch features and offerings
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch offerings (Plans and Add-ons)
        const offeringsResponse = await offeringsService.getAll();
        if (offeringsResponse.status === "success" && offeringsResponse.data) {
          setOfferingPlans(
            [...offeringsResponse.data.RECRUITER.plans].reverse(),
          );
          setOfferingAddons(
            [
              ...offeringsResponse.data.RECRUITER.addons,
              ...offeringsResponse.data.DIRECT.addons,
            ].reverse(),
          );
          if (offeringsResponse.data.LIFECYCLE?.phases) {
            setLifecyclePhases(offeringsResponse.data.LIFECYCLE.phases);
          }
        }

        if (id) {
          // Fetch organization specific features
          const featuresResponse =
            await import("../../../services/api/settings").then((m) =>
              m.featuresService.getAllAvailable(id),
            );
          if (featuresResponse.status === "success" && featuresResponse.data) {
            setFeatures(featuresResponse.data);
          }
        }
      } catch (error) {
        console.error("Error fetching pricing data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // const selectedFeatures = features.filter((f) =>
  const selectedFeaturesForFloat = features.filter((f) =>
    selectedFeatureIds.includes(f.id),
  );

  const getTierIcon = (tier: string | null) => {
    switch (tier?.toUpperCase()) {
      case "STARTER":
        return Zap;
      case "GROWTH":
        return Star;
      case "SCALE":
        return Crown;
      default:
        return Zap;
    }
  };

  const plans = offeringPlans.map((plan) => ({
    name: plan.title,
    tier: plan.tier || "",
    badge: plan.subtitle,
    priceMonthly: parseFloat(plan.monthlyPrice).toLocaleString(),
    priceYearly: parseFloat(plan.yearlyPrice).toLocaleString(),
    features: plan.points,
    icon: getTierIcon(plan.tier || ""),
    popular: plan.tier === "GROWTH",
    isVip: plan.tier === "SCALE",
    integration: parseFloat(
      plan.setupFee || plan.integrationCost || "0",
    ).toLocaleString(),
    integrationServices:
      (plan.costBreakdown?.deploymentCosts?.length ?? 0) > 0
        ? (plan.costBreakdown?.deploymentCosts ?? [])
            .filter((d: any) => d && d.name)
            .map((d: any) => ({
              name: d.name,
              cost: d.price.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }),
            }))
        : plan.setupFee || plan.integrationCost
          ? [
              {
                name: "Standard Onboarding & Training",
                cost: parseFloat(
                  plan.setupFee || plan.integrationCost || "0",
                ).toLocaleString(),
              },
            ]
          : [],
    button: plan.tier === "ENTERPRISE" ? "Contact Sales" : "Get Now",
  }));

  const currentPlan = plans.find((p) => p.tier === selectedTier);
  const planFeatures = currentPlan?.features
    ? currentPlan.features.map((f: any) => ({
        id: f,
        displayName: f,
        price: 0,
      }))
    : [];

  const selectedLifecyclePhasesForFloat = lifecyclePhases
    .filter((p) => selectedLifecyclePhaseIds.includes(p.id) || p.isMandatory)
    .map((p) => ({
      id: p.id.toString(),
      name: p.name,
      displayName: p.name,
      description: p.description,
      price:
        p.setupFee +
        p.monthlySubscription *
          (billingType === "Yearly" ? 12 : billingType === "Quarterly" ? 3 : 1),
      currency: "NGN",
      category: "LIFECYCLE",
    }));

  const handleFeatureRemove = (featureId: string) => {
    handleFeatureSelectionChange(
      selectedFeatureIds.filter((id) => id !== featureId),
    );
    if (selectedDirectAddOnIds.includes(featureId)) {
      handleDirectAddOnToggle(featureId);
    }
    const numId = Number(featureId);
    if (!isNaN(numId) && selectedLifecyclePhaseIds.includes(numId)) {
      handleLifecyclePhaseToggle(numId);
    }
  };

  const [selectedDirectAddOnIds, setSelectedDirectAddOnIds] = useState<
    string[]
  >(
    searchParams
      .get("directAddOnIds")
      ?.split(",")
      .filter((id) => id) || [],
  );

  // const handleDirectAddOnToggle = (id: string) => {

  const handleDirectAddOnToggle = (addonId: string) => {
    setSelectedDirectAddOnIds((prev) => {
      const isSelected = prev.includes(addonId);
      const newIds = isSelected
        ? prev.filter((id) => id !== addonId)
        : [...prev, addonId];

      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("directAddOns", newIds.join(","));
        } else {
          p.delete("directAddOns");
        }
        return p;
      });

      return newIds;
    });
  };

  const handleLifecyclePhaseToggle = (phaseId: number) => {
    setSelectedLifecyclePhaseIds((prev) => {
      const isSelected = prev.includes(phaseId);
      let newIds = isSelected
        ? prev.filter((id) => id !== phaseId)
        : [...prev, phaseId];

      const mvpPhase = lifecyclePhases.find(
        (p) => p.name && p.name.trim().toLowerCase() === "mvp foundation",
      );
      if (mvpPhase) {
        const mvpId = mvpPhase.id;
        const hasOthersSelected = newIds.some((id) => id !== mvpId);

        if (hasOthersSelected) {
          if (!newIds.includes(mvpId)) {
            newIds.push(mvpId);
          }
        } else {
          if (phaseId === mvpId && isSelected) {
            newIds = [];
          }
        }
      }

      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("lifecyclePhaseIds", newIds.join(","));
        } else {
          p.delete("lifecyclePhaseIds");
        }
        return p;
      });

      return newIds;
    });
  };

  const handleFeaturesAddOnToggle = (id: string) => {
    setSelectedFeatureIds((prev) => {
      const newIds = prev.includes(id)
        ? prev.filter((i) => i !== id)
        : [...prev, id];

      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("featureIds", newIds.join(","));
        } else {
          p.delete("featureIds");
        }
        return p;
      });
      return newIds;
    });
  };

  const [open, setOpen] = useState(false);
  const handleModalClose = () => setOpen(false);

  const selectedDirectAddOnItems = offeringAddons.filter((addon) =>
    selectedDirectAddOnIds.includes(addon.id),
  );

  const allSelectedItems: SelectedItem[] = [
    ...planFeatures,
    ...selectedFeaturesForFloat.map((f) => ({
      id: f.id,
      displayName: f.displayName,
      price: f.price,
    })),
    ...selectedDirectAddOnItems.filter(Boolean).map((a) => ({
      id: a.id,
      displayName: a.name || "Unknown Add-on",
      price: typeof a.price === "string" ? parseFloat(a.price) : a.price || 0,
    })),
    ...selectedLifecyclePhasesForFloat,
  ];

  // Helper to calculate total for the floating sidebar
  const calculateFloatingTotal = () => {
    const featuresTotal = allSelectedItems.reduce(
      (sum, item) => sum + (item.price || 0),
      0,
    );
    return featuresTotal.toLocaleString();
  };
  return (
    <div className="font-abel">
      <Navbar title="Login" link="/login" />
      <Hero />
      <PricingToggle
        billingType={billingType}
        setBillingType={(type) => {
          setBillingType(type);
          setSearchParams((p) => {
            p.set(
              "frequency",
              type === "Yearly"
                ? "YEARLY"
                : type === "Quarterly"
                  ? "QUARTERLY"
                  : "MONTHLY",
            );
            return p;
          });
        }}
      />
      <PricingPlans
        billingType={billingType}
        orgId={id ?? ""}
        selectedFeatureIds={selectedFeatureIds}
        selectedFeatures={selectedFeaturesForFloat}
        onFeatureRemove={handleFeatureRemove}
        selectedDirectAddOnIds={selectedDirectAddOnIds}
        onDirectAddOnRemove={handleDirectAddOnToggle}
        selectedTier={selectedTier}
        onTierSelect={(tier) => setSelectedTier(tier)}
        showAllPlans={!selectedTier}
        selectedLifecyclePhaseIds={selectedLifecyclePhaseIds}
        selectedDirectPlan={selectedDirectPlan}
        onDirectPlanChange={setSelectedDirectPlan}
        plans={offeringPlans}
        addons={offeringAddons}
        loading={loading}
      />
      {selectedTier && (
        <div className="flex justify-center mb-10">
          <button
            onClick={() => {
              setSelectedTier(null);
              searchParams.delete("tier");
              setSearchParams(searchParams);
            }}
            className="px-6 py-2 bg-white border border-blue-600 text-blue-600 rounded-full hover:bg-blue-50 transition-colors font-medium flex items-center gap-2"
          >
            <Zap className="w-4 h-4" />
            Compare all plans
          </button>
        </div>
      )}
      <AddOnsAi
        organizationId={id ?? undefined}
        selectedIds={selectedFeatureIds}
        onToggle={handleFeaturesAddOnToggle}
        showDirectAddOns={Object.keys(selectedDirectPlan).length > 0}
        selectedDirectIds={selectedDirectAddOnIds}
        onDirectToggle={handleDirectAddOnToggle}
      />
      {/* <LifecyclePhasesSection
        phases={lifecyclePhases}
        selectedPhaseIds={selectedLifecyclePhaseIds}
        onTogglePhase={handleLifecyclePhaseToggle}
        loading={loading}
      /> */}

      <SelectedFeaturesFloating
        selectedItems={allSelectedItems}
        onRemove={(id) => {
          handleFeatureRemove(id);
          handleDirectAddOnToggle(id);
        }}
        totalPrice={calculateFloatingTotal()}
        billingType={billingType}
        title="Add-on selection"
      />
      {/* <AddOnServices
        onSelectionChange={handleFeatureSelectionChange}
        selectedFeatureIds={selectedFeatureIds}
        organizationId={id ?? ""}
        features={features}
        loading={loading}
      /> */}
      {/* <div className="pt-3 max-w-[645px] mx-auto rounded-15 px-3 my-12">
        <div
          onClick={() => setOpen(true)}
          className="h-[250px] md:h-[355px] w-full bg-[url(/public/images/bg/demo.jpg)] bg-cover bg-center bg-no-repeat mx-auto cursor-pointer flex justify-center items-center rounded-12"
        ></div>
      </div> */}
      <ModalNew
        isOpen={open}
        onClose={handleModalClose}
        title=""
        className="px-0 md:px-0 py-0 md:py-0 max-w-[895px]"
      >
        <YouTubeEmbed
          videoId="j4aqYvtzOz0"
          mobileHeight={350}
          desktopHeight={505}
        />
      </ModalNew>
      {/* <DirectPricingSection billingType={billingType} orgId={id ?? ""} />
      <DirectAddOns /> */}
      {/* <FooterComponent /> */}
    </div>
  );
};

export default PricingPage;
